import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDateDMY } from '../../utils/dateFormat';
import DateInputDMY from '../DateInputDMY';
import SearchableSelect from '@/components/ui/SearchableSelect';
import PhotoCapture from '../PhotoCapture';

const ActivityForm = ({
  newActivity,
  setNewActivity,
  users,
  customers,
  products,
  companySettings,
  onSubmit,
  onCancel
}) => {
  const supportUsers = useMemo(() => users.filter(u => u.role === 'support'), [users]);

  // Pre-filter products that have a serial sold to the selected customer
  const productsForCustomer = useMemo(() => {
    if (!newActivity.customer_id) return [];
    return products.filter(p =>
      p.serial_numbers?.some(s => s.customer_id === newActivity.customer_id && s.status === 'sold')
    );
  }, [products, newActivity.customer_id]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-3xl my-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="sticky top-0 bg-white z-10 border-b">
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl">Create New Activity</CardTitle>
            <Button
              variant="ghost"
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
              type="button"
            >
              ✕
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Title */}
              <div>
                <Label>Title</Label>
                <Input
                  value={newActivity.title}
                  onChange={(e) => setNewActivity({...newActivity, title: e.target.value})}
                  required
                />
              </div>

              {/* Assign To */}
              <div>
                <Label>Assign To</Label>
                <SearchableSelect
                  value={newActivity.assigned_to}
                  onChange={(val) => setNewActivity({...newActivity, assigned_to: val})}
                  options={users.map(user => ({ value: user.id, label: `${user.name} (${user.role})` }))}
                  placeholder="Select User"
                />
              </div>

              {/* Support Staff */}
              <div>
                <Label>Support Staff</Label>
                <SearchableSelect
                  value={newActivity.support_staff}
                  onChange={(val) => setNewActivity({...newActivity, support_staff: val})}
                  options={supportUsers.map(user => ({ value: user.id, label: user.name }))}
                  placeholder="Select Support Staff"
                />
              </div>

              {/* Customer */}
              <div>
                <Label>Customer</Label>
                <SearchableSelect
                  value={newActivity.customer_id}
                  onChange={(val) => setNewActivity({...newActivity, customer_id: val, product_id: '', serial_number: ''})}
                  options={customers.map(customer => ({ value: customer.id, label: `${customer.name} - ${customer.business_vertical || 'N/A'}` }))}
                  placeholder="Select Customer (Optional)"
                />
              </div>
              
              {/* Product Selection */}
              <div>
                <Label>Product</Label>
                {newActivity.customer_id ? (
                  <SearchableSelect
                    value={newActivity.product_id}
                    onChange={(val) => setNewActivity({...newActivity, product_id: val, serial_number: ''})}
                    options={productsForCustomer.map(product => ({ value: product.id, label: `${product.name} - ${product.model || product.category}` }))}
                    placeholder="Select Product"
                  />
                ) : (
                  <div className="w-full border rounded-md p-2 bg-gray-50 flex items-center justify-center text-gray-500">
                    Please select a customer first
                  </div>
                )}
              </div>
              
              {/* Serial Number Selection */}
              <div>
                <Label>Product Serial Number</Label>
                {newActivity.product_id && newActivity.customer_id ? (
                  <SearchableSelect
                    value={newActivity.serial_number}
                    onChange={(val) => setNewActivity({...newActivity, serial_number: val})}
                    options={(() => {
                      const product = products.find(p => p.id === newActivity.product_id);
                      return (product?.serial_numbers || [])
                        .filter(s => s.customer_id === newActivity.customer_id && s.status === 'sold')
                        .map((serial) => ({
                          value: serial.serial_number,
                          label: `${serial.serial_number} (Sold: ${serial.sale_date ? formatDateDMY(serial.sale_date) : 'N/A'})`
                        }));
                    })()}
                    placeholder="Select Serial Number"
                  />
                ) : (
                  <div className="w-full border rounded-md p-2 bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                    {!newActivity.customer_id ? 'Select customer and product first' : 'Select a product first'}
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  Only serial numbers assigned to the selected customer are shown
                </p>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <Label>Description</Label>
                <textarea
                  className="w-full border rounded-md p-2"
                  rows="3"
                  value={newActivity.description}
                  onChange={(e) => setNewActivity({...newActivity, description: e.target.value})}
                />
              </div>

              {/* Status */}
              <div>
                <Label>Status</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={newActivity.status}
                  onChange={(e) => setNewActivity({...newActivity, status: e.target.value})}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              {/* Activity Type */}
              <div>
                <Label>Activity Type</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={newActivity.activity_type}
                  onChange={(e) => setNewActivity({...newActivity, activity_type: e.target.value})}
                >
                  <option value="demo_poc">Demo/POC</option>
                  <option value="warranty">Warranty</option>
                  <option value="service_call">Service Call</option>
                  <option value="periodic_visit">Periodic Visit</option>
                  <option value="new_installation">New Installation</option>
                  <option value="others">Others</option>
                </select>
              </div>

              {/* Created Date — defaults to today; editable for back-dating a visit */}
              <div>
                <Label>Created Date</Label>
                <DateInputDMY
                  value={newActivity.created_date}
                  onChange={(e) => setNewActivity({...newActivity, created_date: e.target.value})}
                  data-testid="activity-created-date"
                />
                <p className="text-xs text-gray-500 mt-1">Defaults to today. Change to back-date a visit / activity.</p>
              </div>

              {/* Due Date */}
              <div>
                <Label>Due Date (Optional)</Label>
                <DateInputDMY
                  value={newActivity.due_date}
                  onChange={(e) => setNewActivity({...newActivity, due_date: e.target.value})}
                />
              </div>
              
              {/* Completion Fields - Show only when status is completed */}
              {newActivity.status === 'completed' && (
                <>
                  <div>
                    <Label>Invoice Number</Label>
                    <Input
                      value={newActivity.invoice_number}
                      onChange={(e) => setNewActivity({...newActivity, invoice_number: e.target.value})}
                      placeholder="INV-2024-001"
                    />
                  </div>
                  <div>
                    <Label>Work Order Number</Label>
                    <Input
                      value={newActivity.work_order_no}
                      onChange={(e) => setNewActivity({...newActivity, work_order_no: e.target.value})}
                      placeholder="WO-2024-001"
                    />
                  </div>
                  <div>
                    <Label>Total Amount ({companySettings?.currency || 'USD'})</Label>
                    <Input
                      type="number"
                      value={newActivity.total_amount}
                      onChange={(e) => setNewActivity({...newActivity, total_amount: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Next Maintenance Due Date</Label>
                    <DateInputDMY
                      value={newActivity.next_maintenance_date}
                      onChange={(e) => setNewActivity({...newActivity, next_maintenance_date: e.target.value})}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This will be synced to the product's serial number record
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Visit Photos — capture on-site evidence (works in both
                the web browser and the Capacitor native shell). */}
            <div className="pt-2 border-t">
              <PhotoCapture
                value={newActivity.photos || []}
                onChange={(next) => setNewActivity({ ...newActivity, photos: next })}
                limit={5}
                label="📷 Visit Photos"
                testId="activity-photos"
                sourcePreference="PROMPT"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-blue-700 to-green-700">
                Create Activity
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityForm;
